<template>
    <div v-if="!loading && users" class="w-full">
        <DataTable :value="users" responsiveLayout="scroll" ref="dt" dataKey="id"
            :rows="20" :rowsPerPageOptions="[20, 50, 100, 200, 300]" :totalRecords="totalUsers" paginator
            @page="onPage($event)" @sort="onSort($event)" :loading="loading" 
            @filter="onFilter($event)" v-model:filters="filters" filterDisplay="menu" :globalFilterFields="globalFields" >

            <Column field="id" header="" filterMatchMode="contains" :showFilterMenu="false" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row">
                            <span @click="goTo(`/admin/user/${data.id}`)" 
                                class="text-xs flex my-auto overflow-hidden hover:text-primary cursor-pointer" style="max-width:5em;">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>...
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="flex border-0" />
                        <Button @click="goTo(`/admin/user/${data.id}`, true)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                    </div>
                </template>
            </Column>

            <Column v-if="isUserInGroup('admin_wallets', accessCodes)" field="merchant_wallet_id" filterField="merchant_wallet_id" filterMatchMode="contains" :showFilterMenu="false" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">WID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <span v-if="data.merchant_wallet_id"
                        class="text-sm flex flex-row vertical-align-middle">
                        <div class="flex flex-row gap-1 my-auto">
                            <span @click="goTo(`/admin/wallet/${data.merchant_wallet_id}`)" 
                                class="text-xs flex my-auto overflow-hidden hover:text-primary cursor-pointer" style="max-width:5em;">
                                <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                    {{ data.merchant_wallet_id }}
                                </span>
                            </span>
                            <span>...</span>
                        </div>
                        <Button @click="copy(data.merchant_wallet_id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="flex border-0" />
                        <Button @click="goTo(`/admin/wallet/${data.merchant_wallet_id}`, true)" 
                            icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="flex border-0" />
                    </span>
                </template>
            </Column>

            <Column field="name">
                <template #header>
                    <span class="text-xs font-medium">Имя</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs flex font-medium overflow-hidden" style="max-width:10em">
                        {{ data.name }}
                    </span>
                </template>
            </Column>

            <Column field="balance_usdt">
                <template #header>
                    <span class="text-xs font-medium">Б.USDT</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ getFormatAmount(getUserBalance(data.merchant_wallet_id, 'usdt'), 'USDT', 0) }}</span>
                </template>
            </Column>

            <Column field="balance_rub">
                <template #header>
                    <span class="text-xs font-medium">Б.RUB</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ getFormatAmount(getUserBalance(data.merchant_wallet_id, 'rub'), 'RUB', 0) }}</span>
                </template>
            </Column>

            <Column v-if="isUserInGroup('super_admin', accessCodes)" field="project_id" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Проект</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="projects" optionValue="value" optionLabel="label" placeholder="Все" class="p-column-filter" :showClear="true"/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs">{{ getProjectLabel(data.project_id) }}</span>
                </template>
            </Column>

            <Column field="payment_systems">
                <template #header>
                    <span class="text-xs font-medium">Платежные системы</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-column gap-1">
                        <div v-if="data.payment_systems && data.payment_systems.length > 0" class="flex flex-wrap gap-1">
                            <Tag v-for="(ps, idx) in data.payment_systems" :key="idx" 
                                :value="ps.system || ps.code" 
                                :severity="ps.kyc_required ? 'warning' : 'success'" 
                                class="text-xs" size="small"/>
                        </div>
                        <span v-else class="text-xs text-500">—</span>
                    </div>
                </template>
            </Column>

            <Column field="urls" v-if="false">
                <template #header>
                    <span class="text-xs font-medium">URLs</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-column gap-1">
                        <div v-if="data.success_url || data.fail_url || data.webhook_url" class="flex flex-column gap-1">
                            <Tag v-if="data.success_url" value="Success" severity="success" class="text-xs" size="small"/>
                            <Tag v-if="data.fail_url" value="Fail" severity="danger" class="text-xs" size="small"/>
                            <Tag v-if="data.webhook_url" value="Webhook" severity="info" class="text-xs" size="small"/>
                        </div>
                        <span v-else class="text-xs text-500">—</span>
                    </div>
                </template>
            </Column>
            
            <Column field="created_at">
                <template #header>
                    <span class="text-xs font-medium">Дата создания</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs flex">
                        {{ formatDate(data.created_at) }}
                    </span>
                </template>
            </Column>

            <Column v-if="isUserInGroup('admin_users_edit', accessCodes)">
                <template #header>
                    <span class="text-xs font-medium">Действия</span>
                </template>
                <template #body="{ data }">
                    <EditUser v-if="isUserInGroup('admin_users_edit', accessCodes)" :user="data" @updated="onUserUpdated"/>
                </template>
            </Column>

            <Column field="email">
                <template #header>
                    <span class="text-xs font-medium">Email</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs flex">
                        {{ data.email }}
                    </span>
                </template>
            </Column>

            <template #footer>
                <span class="text-sm text-600 font-medium">
                    Всего {{ totalUsers }} пользователей 
                </span>
            </template>

        </DataTable>
    </div>
</template>

<script>
import Connect from '@/assets/js/connect.js'
import Helper from '@/assets/js/helper.js'
import EditUser from '@/components/admin/user/edit.vue'

export default {
    props: {
        users: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        totalUsers: {
            type: Number,
            default: 0
        },
        accounts: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            lazyParams: {},
            filters: {
                id: {value: '', matchMode: 'contains'}, 
                project_id: {value: '', matchMode: 'equals'},
            },
            sort: {
                field: 'id',
                order: -1
            },
            projects: [
                {
                    value: '699ea1f3c9b225f0e92393c6', 
                    label: 'X-HUB'
                },
                {
                    value: '699ead66c13e000266f72745',
                    label: 'PaySido'
                },
                {
                    value: '69ddfa476b47e9aea51ae0e2',
                    label: 'PaySame'
                }
            ],
            globalFields: ['id', 'project_id'],
        }
    },
    components: {
        EditUser
    },
    computed: {
        accessCodes: function () { return this.$store.getters.StateUserAccessCodes },
        getUserBalance() {
            return (merchant_wallet_id, currency) => {
                if (!merchant_wallet_id || !this.accounts || !this.accounts.length) return '—'
                const account = this.accounts.find(a => a.wallet_id === merchant_wallet_id && a.currency === currency)
                return account ? account.balance : '—'
            }
        }
    },
    methods: {
        getProjectLabel(project_id){
            const project = this.projects.find(p => p.value === project_id)
            return (project) ? project.label : project_id
        },
        getFormatAmount(value, currency, digits=2) {
            return this.helper.format_amount(value, currency, digits)
        },
        getShort(text, length){
            if(text.length <= length) return text
            return '...' + text.substring(text.length - length)
        },
        isUserInGroup(user, group) {
            return this.helper.isUserInGroup(user, group)
        },
        goTo(url, newTab = false) {
            if(!url) return
            const routeData = this.$router.resolve({ path: url }) 
            if(newTab) window.open(routeData.href, '_blank');
            else this.$router.push(routeData.href)
        },
        formatDate(dateStr){
            return this.helper.formatDate(dateStr)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        onPage(event) {
            this.lazyParams = event
            this.$emit('lazyParams', this.lazyParams)
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'date_created',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.$emit('lazyParams', this.lazyParams)
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.$emit('filter', this.filters)
        },
        onRowSelect() {
            this.selectAll = this.selectedCustomers.length === this.totalUsers
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        onUserUpdated(updatedUser) {
            this.$emit('userUpdated', updatedUser)
        },
    },
    async mounted() {
        this.lazyParams = {
            first: 0,
            rows: 10,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
        };
    },
    async beforeCreate(){
        this.connect = new Connect()
        this.helper = new Helper()
    },
}
</script>